import React, { useEffect, useState, useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import SaveIcon from '@mui/icons-material/Save'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import DeleteIcon from '@mui/icons-material/Delete'
import { makeGetRequest, makePostRequest } from 'utils/api'

function ColumnMappingStep({
  fileData,
  columnMapping,
  setColumnMapping,
  amountMode,
  setAmountMode,
  accountInfo,
  mappingTemplates,
  setMappingTemplates,
  setError,
  setLoading,
  onNext,
  onBack,
}) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [saveTemplateDialog, setSaveTemplateDialog] = useState(false)
  const [loadTemplateDialog, setLoadTemplateDialog] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [setAsDefault, setSetAsDefault] = useState(false)

  // Le fichier porte soit UNE colonne montant signée, soit DEUX colonnes
  // Crédit / Débit (exports bancaires fréquents). En mode crédit/débit le
  // signe est déterminé par la paire de colonnes : la colonne « sens » et
  // l'étape de logique de signe n'ont plus lieu d'être.
  const internalFields = useMemo(() => {
    const base = [
      { value: 'date', label: t('mesComptes.import.step3.fieldDate'), required: true },
      { value: 'libelle', label: t('mesComptes.import.step3.fieldLibelle'), required: true },
    ]
    const amount =
      amountMode === 'credit_debit'
        ? [
            { value: 'credit', label: t('mesComptes.import.step3.fieldCredit'), required: true },
            { value: 'debit', label: t('mesComptes.import.step3.fieldDebit'), required: true },
          ]
        : [{ value: 'montant', label: t('mesComptes.import.step3.fieldMontant'), required: true }]
    const optional = [
      { value: 'subcategory', label: t('mesComptes.import.step3.fieldSubcategory'), required: false },
    ]
    if (amountMode !== 'credit_debit') {
      optional.push({ value: 'sens', label: t('mesComptes.import.step3.fieldSens'), required: false })
    }
    return [...base, ...amount, ...optional]
  }, [t, amountMode]) // LAP-733 (GE022)

  const handleAmountModeChange = (_e, mode) => {
    if (!mode || mode === amountMode) return
    setAmountMode(mode)
    // Purge le mapping de l'autre mode pour ne jamais envoyer montant ET
    // crédit/débit en même temps au backend.
    const cleaned = { ...columnMapping }
    if (mode === 'credit_debit') {
      delete cleaned.montant
      delete cleaned.sens
    } else {
      delete cleaned.credit
      delete cleaned.debit
    }
    setColumnMapping(cleaned)
  }

  const loadTemplates = useCallback(async () => {
    try {
      const response = await makeGetRequest('/api/Finance/import/templates/', {}, true)
      if (response.status === 200) {
        setMappingTemplates(response.data)
      }
    } catch (error) {
      console.error('Error loading templates:', error)
    }
  }, [setMappingTemplates])

  useEffect(() => {
    // Load mapping templates
    loadTemplates()
  }, [loadTemplates])


  const handleMappingChange = (internalField, fileColumn) => {
    setColumnMapping({
      ...columnMapping,
      [internalField]: fileColumn,
    })
  }

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setError(t('mesComptes.import.errors.templateName')) // LAP-733 (GE022)
      return
    }

    setLoading(true)
    try {
      const response = await makePostRequest(
        '/api/Finance/import/templates/',
        {
          mapping_name: templateName,
          column_mapping: columnMapping,
          is_default: setAsDefault,
        },
        {},
        true
      )

      if (response.status === 201) {
        setSaveTemplateDialog(false)
        setTemplateName('')
        setSetAsDefault(false)
        await loadTemplates()
        setError(null)
      } else {
        setError(response.data?.error || t('mesComptes.import.errors.saveTemplate')) // LAP-733 (GE022)
      }
    } catch (error) {
      setError(error.response?.data?.error || t('mesComptes.import.errors.saveTemplateGeneric')) // LAP-733 (GE022)
    } finally {
      setLoading(false)
    }
  }

  const handleLoadTemplate = (template) => {
    const mapping = template.column_mapping || {}
    setColumnMapping(mapping)
    // Recale le mode montant sur le contenu du modèle chargé
    setAmountMode(mapping.credit || mapping.debit ? 'credit_debit' : 'single')
    setLoadTemplateDialog(false)
  }

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm(t('mesComptes.import.step3.confirmDeleteTemplate'))) return // LAP-733 (GE022)

    setLoading(true)
    try {
      const response = await makePostRequest(
        `/api/Finance/import/templates/${templateId}/`,
        {},
        { method: 'DELETE' },
        true
      )

      if (response.status === 200) {
        await loadTemplates()
      }
    } catch (error) {
      setError(t('mesComptes.import.errors.deleteTemplate')) // LAP-733 (GE022)
    } finally {
      setLoading(false)
    }
  }

  const validateMapping = () => {
    // Validate mapping
    const missingFields = internalFields.filter((f) => f.required && !columnMapping[f.value])

    if (missingFields.length > 0) {
      // Affiche les libellés lisibles (pas les clés techniques) pour guider
      // l'utilisateur vers les lignes de mapping à compléter.
      setError(
        t('mesComptes.import.errors.requiredFields', {
          fields: missingFields.map((f) => f.label).join(', '),
        })
      ) // LAP-733 (GE022)
      return false
    }

    setError(null)
    return true
  }

  const handleNext = () => {
    if (validateMapping()) {
      onNext()
    }
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('mesComptes.import.step3.title') /* LAP-733 (GE022) */}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('mesComptes.import.step3.description') /* LAP-733 (GE022) */}
      </Typography>

      {/* Account Info Display */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('mesComptes.import.step3.importingToAccount') /* LAP-733 (GE022) */}
        </Typography>
        <Typography variant="body1" fontWeight={600}>
          {t('mesComptes.import.step3.ibanPrefix')} {accountInfo.IBAN || accountInfo.NumeroCompte || '—'}
          {/* LAP-733 (GE022) */}
        </Typography>
      </Paper>

      {/* Template Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<FolderOpenIcon />}
          onClick={() => setLoadTemplateDialog(true)}
          disabled={mappingTemplates.length === 0}
        >
          {t('mesComptes.import.step3.loadTemplate') /* LAP-733 (GE022) */}
        </Button>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={() => setSaveTemplateDialog(true)}
          disabled={Object.keys(columnMapping).length === 0}
        >
          {t('mesComptes.import.step3.saveAsTemplate') /* LAP-733 (GE022) */}
        </Button>
      </Box>

      {/* Amount mode: one signed column OR credit/debit pair */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('mesComptes.import.step3.amountMode.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('mesComptes.import.step3.amountMode.description')}
        </Typography>
        <ToggleButtonGroup
          value={amountMode}
          exclusive
          onChange={handleAmountModeChange}
          size="small"
          color="primary"
        >
          <ToggleButton value="single" sx={{ textTransform: 'none', px: 2 }}>
            {t('mesComptes.import.step3.amountMode.single')}
          </ToggleButton>
          <ToggleButton value="credit_debit" sx={{ textTransform: 'none', px: 2 }}>
            {t('mesComptes.import.step3.amountMode.creditDebit')}
          </ToggleButton>
        </ToggleButtonGroup>
        {amountMode === 'credit_debit' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {t('mesComptes.import.step3.amountMode.creditDebitHint')}
          </Typography>
        )}
      </Paper>

      {/* Column Mapping */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('mesComptes.import.step3.columnMapping') /* LAP-733 (GE022) */}
        </Typography>

        <Grid container spacing={2}>
          {internalFields.map((field) => (
            <Grid item xs={12} key={field.value}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={600}>
                    {field.label}
                  </Typography>
                  {field.required && (
                    <Chip
                      label={t('mesComptes.import.step3.required') /* LAP-733 (GE022) */}
                      size="small"
                      color="error"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
                <ArrowForwardIcon color="action" />
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>
                      {t('mesComptes.import.step3.selectColumn') /* LAP-733 (GE022) */}
                    </InputLabel>
                    <Select
                      value={columnMapping[field.value] || ''}
                      onChange={(e) => handleMappingChange(field.value, e.target.value)}
                      label={t('mesComptes.import.step3.selectColumn') /* LAP-733 (GE022) */}
                    >
                      <MenuItem value="">
                        <em>{t('mesComptes.import.step3.none') /* LAP-733 (GE022) */}</em>
                      </MenuItem>
                      {fileData?.columns.map((col) => (
                        <MenuItem key={col} value={col}>
                          {col}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Navigation */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={onBack}>
          {t('mesComptes.import.back') /* LAP-733 (GE022) */}
        </Button>
        <Button variant="contained" onClick={handleNext}>
          {t('mesComptes.import.step3.nextButton') /* LAP-733 (GE022) */}
        </Button>
      </Box>

      {/* Save Template Dialog */}
      <Dialog open={saveTemplateDialog} onClose={() => setSaveTemplateDialog(false)}>
        <DialogTitle>
          {t('mesComptes.import.step3.saveDialog.title') /* LAP-733 (GE022) */}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('mesComptes.import.step3.saveDialog.templateName') /* LAP-733 (GE022) */}
            fullWidth
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
          />
          <Box sx={{ mt: 2 }}>
            <Button
              variant={setAsDefault ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setSetAsDefault(!setAsDefault)}
            >
              {setAsDefault
                ? t('mesComptes.import.step3.saveDialog.defaultTemplate')
                : t('mesComptes.import.step3.saveDialog.setAsDefault')}
              {/* LAP-733 (GE022) */}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveTemplateDialog(false)}>
            {t('mesComptes.import.cancel') /* LAP-733 (GE022) */}
          </Button>
          <Button onClick={handleSaveTemplate} variant="contained">
            {t('mesComptes.import.step3.saveDialog.save') /* LAP-733 (GE022) */}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Load Template Dialog */}
      <Dialog
        open={loadTemplateDialog}
        onClose={() => setLoadTemplateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('mesComptes.import.step3.loadDialog.title') /* LAP-733 (GE022) */}
        </DialogTitle>
        <DialogContent>
          <List>
            {mappingTemplates.map((template) => (
              <ListItem
                key={template.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteTemplate(template.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                }
                disablePadding
              >
                <ListItemButton onClick={() => handleLoadTemplate(template)}>
                  <ListItemText
                    primary={template.mapping_name}
                    secondary={t('mesComptes.import.step3.loadDialog.updatedPrefix', {
                      date: new Date(template.updated_at).toLocaleDateString(),
                    }) /* LAP-733 (GE022) */}
                  />
                  {template.is_default && (
                    <Chip
                      label={t('mesComptes.import.step3.loadDialog.default') /* LAP-733 (GE022) */}
                      size="small"
                      color="primary"
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadTemplateDialog(false)}>
            {t('mesComptes.import.step3.loadDialog.close') /* LAP-733 (GE022) */}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ColumnMappingStep
